import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../../components/Header';
import IntakeBanner from '../../components/IntakeBanner';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import BookTrialButton from '../../components/BookTrialButton';

/**
 * Shared layout for all legal / policy pages.
 *
 * Each legal page passes:
 *   - title:        Page H1
 *   - effectiveDate: e.g. "Effective from 1 March 2026"
 *   - children:     Page body (sectioned headings + paragraphs)
 *
 * Content blocks should use the helper components below for consistent styling.
 */
const LegalLayout = ({ title, effectiveDate, pageTestId, children }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = `${title} | Read Right Academy`;
    return () => {
      document.title = 'Read Right Academy';
    };
  }, [title]);

  return (
    <>
      <IntakeBanner />
      <Header />
      <main className="bg-white pt-44 pb-24" data-testid={pageTestId}>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#3B4BA0] transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-3">
            {title}
          </h1>
          {effectiveDate && (
            <p className="text-sm text-gray-500 border-b border-gray-100 pb-5 mb-8">
              {effectiveDate}
            </p>
          )}

          <div className="legal-body space-y-6">{children}</div>

          {/* Small footer note */}
          <div className="mt-16 pt-8 border-t border-gray-100 text-sm text-gray-500">
            <p>
              Have a question about this policy? Email{' '}
              <a
                href="mailto:teacherkalah@gmail.com"
                className="text-[#3B4BA0] hover:underline"
              >
                teacherkalah@gmail.com
              </a>{' '}
              or message us on WhatsApp at{' '}
              <a
                href="https://wa.me/60166291687"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3B4BA0] hover:underline"
              >
                +60 16-629 1687
              </a>
              .
            </p>
          </div>
        </article>
      </main>
      <Footer />
      <WhatsAppButton />
      <BookTrialButton />
    </>
  );
};

// ---------- Reusable typography helpers ----------

export const LegalSection = ({ heading, children }) => (
  <section className="space-y-3">
    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 mb-2">
      {heading}
    </h2>
    {children}
  </section>
);

export const LegalParagraph = ({ children }) => (
  <p className="text-base text-gray-700 leading-relaxed">{children}</p>
);

export const LegalList = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map((item, i) => (
      <li
        key={i}
        className="flex items-start gap-3 text-base text-gray-700 leading-relaxed"
      >
        <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#3B4BA0] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const LegalNote = ({ children, tone = 'info' }) => {
  const tones = {
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    warn: 'bg-amber-50 border-amber-200 text-amber-900',
    safe: 'bg-emerald-50 border-emerald-200 text-emerald-900'
  };
  return (
    <div
      className={`rounded-xl border-l-4 ${tones[tone]} px-5 py-4 text-sm leading-relaxed`}
    >
      {children}
    </div>
  );
};

export default LegalLayout;
